import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  VStack
} from '@chakra-ui/react'
import { useStoreActions, useStoreState } from 'easy-peasy'
import React, { type ReactElement, useEffect } from 'react'
import {
  useReminderControllerDeleteReminder,
  useReminderControllerGetReminder
} from '~/clinician-api'
import type { SetReminderDto } from '~/clinician-api/models'
import { ReminderHandIcon } from '../../../components/icons'

interface SessionReminderSectionProps {
  onClick: () => void
}

export const SessionReminderSection: React.FC<SessionReminderSectionProps> = ({
  onClick
}) => {
  const { isOpen: isModalOpen } = useStoreState(
    (state: any) => state.modals.clinicianSessionReminder
  )

  const { data: reminder, refetch: refetchReminder } =
    useReminderControllerGetReminder()

  const { mutate: deleteReminder } = useReminderControllerDeleteReminder({
    mutation: {
      onSuccess: () => {
        refetchReminder()
      }
    }
  })

  useEffect(() => {
    if (!isModalOpen) {
      refetchReminder()
    }
  }, [isModalOpen])

  const onClickDelete = () => {
    deleteReminder()
  }

  const reminderText = formatReminderText(reminder)

  return (
    <VStack w="100%" spacing={4} align="stretch">
      <Text fontWeight="bold">Not ready to record yet?</Text>
      <Flex
        align="center"
        justify="space-between"
        bg="var(--BP-Blue-040, #F6F7FE)"
        borderRadius="8px"
        p={4}
        w="100%"
        boxShadow="sm"
        position="relative"
      >
        <Flex align="center">
          <Box
            bg={useColorModeValue('blue.50', 'blue.900')}
            borderRadius="12px"
            p={3}
            mr={4}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <ReminderHandIcon />
          </Box>
          <Text>{reminderText}</Text>
        </Flex>
        <Flex align="end">
          <Button
            fontSize="sm"
            variant={reminder ? 'outline' : 'solid'}
            data-click-event-name="Sessions Index -> Clicked Set Reminder"
            data-click-text={reminder ? 'Edit reminder' : 'Schedule reminder'}
            onClick={onClick}
            h="32px"
            py={1}
          >
            {reminder ? 'Edit' : 'Schedule reminder'}
          </Button>
          {reminder && (
            <Button
              fontSize="sm"
              variant="outline"
              data-click-event-name="Sessions Index -> Clicked Delete Reminder"
              data-click-text="Delete reminder"
              color="red"
              onClick={onClickDelete}
              h="32px"
              py={1}
            >
              Delete
            </Button>
          )}
        </Flex>
      </Flex>
    </VStack>
  )
}

function formatReminderText(
  reminder: SetReminderDto | undefined
): ReactElement {
  if (!reminder) {
    return <span>Get a reminder 15 min before your next session</span>
  }

  const method =
    reminder.contactMethod === 'both'
      ? 'email and text'
      : reminder.contactMethod === 'email'
        ? 'email'
        : 'text'

  const date = new Date(reminder.nextAppointmentDateTime)
  // Reminder is 15 minutes before the appointment
  // So we need to subtract 15 minutes from the date
  date.setMinutes(date.getMinutes() - 15)
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  })

  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })

  return (
    <span>
      We'll {method} you on{' '}
      <strong>
        {formattedDate} at {formattedTime}
      </strong>
    </span>
  )
}
